import {
  ActionIcon,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Flex,
  Grid,
  Paper,
  Text,
  useMantineTheme,
  Stack,
  Divider,
  Badge,
  Group,
  ThemeIcon,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { 
  IconCheck, 
  IconPlus, 
  IconEdit, 
  IconMapPin, 
  IconCreditCard, 
  IconReceipt2,
  IconCircleCheckFilled,
  IconChevronRight
} from '@tabler/icons-react';
import React, { useContext, useEffect, useState } from 'react';
import AddAddressModal from '../components/AddAddressModal';
import DeliveryTimeModal from '../components/DeliveryTimeModal';
import { api } from '../services/master.service';
import { showErrorToast, showSuccessToast } from '../utilities/Toast';
import { useNavigate } from 'react-router';
import { Tick } from '../components/Tick';
import { CartContext } from '../App';
import { is_mobile_view } from '../config';
import { motion, AnimatePresence } from 'framer-motion';
import { Player } from '@lottiefiles/react-lottie-player';

function CheckoutPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  
  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;

  const [cart, setCart] = useState({});
  const { cartItemCount, setCartItemCount } = useContext(CartContext);
  const [addresses, setAddresses] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressModalOpened, setAddressModalOpened] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [editData, setEditData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deliveryTimeModal, setDeliveryTimeModal] = useState(false);

  useEffect(() => {
    api.get_cart().then((res) => {
      setCart(res.data.data);
      const hasProducts = res.data.data.cartitem?.length > 0;
      const hasBundles = res.data.data.cart_bundles?.length > 0;
      if (!hasProducts && !hasBundles) navigate('/');
    });
    getDataAddress();
  }, []);

  const getDataAddress = () => {
    api.get_addresses().then((res) => {
      setAddresses(res.data.data || []);
      if (res.data.data?.length > 0 && !selectedAddress) {
        setSelectedAddress(res.data.data[0].id);
      }
    });
  };

  const createOrder = (params) => {
    setIsLoading(true);
    api.create_order({
      order_type: paymentMethod,
      address_id: selectedAddress,
      payment_id: params?.payment_id || null,
      expected_delivery_time: params?.expected_delivery_time || null,
      description: '',
      city_id: localStorage.getItem('city_id'),
      counter_id: localStorage.getItem('counter_id'),
    }).then((res) => {
      setIsLoading(false);
      if (res.data.success) {
        setCartItemCount(0);
        setCheckoutSuccess(true);
      } else {
        showErrorToast({ title: 'Error', message: res.data.message });
      }
    });
  };

  const handleDeliveryTimeSubmit = (selectedTimeId) => {
    setDeliveryTimeModal(false);
    if (!selectedTimeId) {
      showErrorToast({ title: "Error", message: "Please select delivery time" });
      return;
    }
    createOrder({ expected_delivery_time: selectedTimeId });
  };

  if (checkoutSuccess) {
    return (
      <Container size="sm" py={100} style={{ marginTop: isMobileScreen ? 80 : 120 }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <Paper p={40} radius="xl" withBorder sx={{ textAlign: 'center', boxShadow: theme.shadows.xl }}>
            <Player autoplay loop src="/lottie/success-circle-check.json" style={{ height: '200px', width: '200px' }} />
            <Text fw={800} size={32} color="green.7" mt={20}>Order Placed!</Text>
            <Text size="md" color="dimmed" mt={10} mb={30}>
              Your shopping experience was great. We'll notify you once your order is out for delivery.
            </Text>
            <Button size="lg" radius="md" fullWidth onClick={() => navigate('/')}>
              Continue Shopping
            </Button>
          </Paper>
        </motion.div>
      </Container>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: theme.colors.gray[0], 
      paddingTop: isMobileScreen ? 140 : 180,
      paddingBottom: 100 
    }}>
      <Container size="xl">
        <Box mb={30} px={10}>
          <Breadcrumbs separator={<IconChevronRight size={12} />}>
            <Text size="sm" color="dimmed">Home</Text>
            <Text size="sm" color="dimmed">Cart</Text>
            <Text size="sm" fw={700}>Checkout</Text>
          </Breadcrumbs>
          <Text fw={800} size={28} mt={10}>Complete Your Order</Text>
        </Box>

        <Grid gutter="xl">
          <Grid.Col span={12} md={8}>
            <Stack spacing="xl">
              {/* ADDRESS SECTION */}
              <Paper p="xl" radius="lg" shadow="xs">
                <Group position="apart" mb="xl">
                  <Group>
                    <ThemeIcon size={40} radius="md" variant="light" color="blue">
                      <IconMapPin size={22} />
                    </ThemeIcon>
                    <Text fw={700} size="lg">Delivery Address</Text>
                  </Group>
                  <Button variant="subtle" leftIcon={<IconPlus size={16} />} onClick={() => { setIsEditing(false); setEditData({}); setAddressModalOpened(true); }}>
                    Add New
                  </Button>
                </Group>

                <Grid>
                  {addresses.map((addr) => {
                    const isSelected = selectedAddress === addr.id;
                    return (
                      <Grid.Col key={addr.id} span={12} sm={6}>
                        <Paper 
                          withBorder 
                          p="md" 
                          radius="md"
                          onClick={() => setSelectedAddress(addr.id)}
                          sx={{ 
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            borderColor: isSelected ? theme.colors.blue[5] : theme.colors.gray[2],
                            backgroundColor: isSelected ? theme.colors.blue[0] : theme.white,
                            boxShadow: isSelected ? theme.shadows.sm : 'none',
                            '&:hover': { transform: 'translateY(-2px)' }
                          }}
                        >
                          {isSelected && (
                            <Box sx={{ position: 'absolute', top: 12, right: 12, color: theme.colors.blue[6] }}>
                              <IconCircleCheckFilled size={20} />
                            </Box>
                          )}
                          <Text fw={700} size="sm" mb={4}>{addr.address_title}</Text>
                          <Text size="xs" color="dimmed" lineClamp={3}>
                            {addr.address_line_1}, {addr.address_line_2 && addr.address_line_2 + ','} {addr.city}, {addr.pincode}
                          </Text>
                          <Text size="xs" fw={600} mt={8}>{addr.phone_no}</Text>
                          
                          <ActionIcon 
                            size="sm" 
                            variant="subtle" 
                            sx={{ position: 'absolute', bottom: 12, right: 12 }}
                            onClick={(e) => { e.stopPropagation(); setEditData(addr); setIsEditing(true); setAddressModalOpened(true); }}
                          >
                            <IconEdit size={14} />
                          </ActionIcon>
                        </Paper>
                      </Grid.Col>
                    );
                  })}
                </Grid>
              </Paper>

              {/* PAYMENT SECTION */}
              <Paper p="xl" radius="lg" shadow="xs">
                <Group mb="xl">
                  <ThemeIcon size={40} radius="md" variant="light" color="orange">
                    <IconCreditCard size={22} />
                  </ThemeIcon>
                  <Text fw={700} size="lg">Payment Method</Text>
                </Group>
                
                <Grid>
                  <Grid.Col span={12} sm={6}>
                    <Paper 
                      withBorder 
                      p="md" 
                      radius="md"
                      onClick={() => setPaymentMethod('cod')}
                      sx={{ 
                        cursor: 'pointer',
                        borderColor: paymentMethod === 'cod' ? theme.colors.orange[5] : theme.colors.gray[2],
                        backgroundColor: paymentMethod === 'cod' ? theme.colors.orange[0] : theme.white,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                      }}
                    >
                      <ThemeIcon variant="light" color="orange" radius="xl">
                        <IconCheck size={16} />
                      </ThemeIcon>
                      <Box>
                        <Text fw={700} size="sm">Cash on Delivery</Text>
                        <Text size="xs" color="dimmed">Pay when you receive</Text>
                      </Box>
                    </Paper>
                  </Grid.Col>
                </Grid>
              </Paper>
            </Stack>
          </Grid.Col>

          <Grid.Col span={12} md={4}>
            <Paper p={isMobileScreen ? 'md' : 'xl'} radius="lg" shadow="sm" sx={{ position: 'sticky', top: 180 }}>
              <Group mb="xl">
                <ThemeIcon size={40} radius="md" variant="light" color="teal">
                  <IconReceipt2 size={22} />
                </ThemeIcon>
                <Text fw={700} size="lg">Order Summary</Text>
              </Group>

              <Stack spacing="sm">
                <Flex justify="space-between">
                  <Text size="sm" color="dimmed">Subtotal</Text>
                  <Text size="sm" fw={600}>₹{cart.sub_total}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text size="sm" color="dimmed">Tax (Inclusive)</Text>
                  <Text size="sm" fw={600}>₹{cart.tax_total}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text size="sm" color="dimmed">Shipping</Text>
                  <Text size="sm" fw={600} color={cart.delivery > 0 ? 'inherit' : 'teal.6'}>
                    {cart.delivery > 0 ? `₹${cart.delivery}` : 'FREE'}
                  </Text>
                </Flex>
                {Number(cart.packaging || 0) > 0 && (
                  <Flex justify="space-between">
                    <Text size="sm" color="dimmed">Packing Charge</Text>
                    <Text size="sm" fw={600}>₹{cart.packaging}</Text>
                  </Flex>
                )}

                {Number(cart.discount_total || 0) > 0 && (
                  <Flex justify="space-between">
                    <Text size="sm" color="teal.7">Total Discount</Text>
                    <Text size="sm" fw={700} color="teal.7">- ₹{cart.discount_total}</Text>
                  </Flex>
                )}
                <Divider my="sm" />
                <Flex justify="space-between" align="baseline">
                  <Text fw={800} size="xl">Total</Text>
                  <Text fw={800} size="24px" color="orange.7">₹{cart.total}</Text>
                </Flex>

                {!selectedAddress && (
                  <Text size="sm" color="red.6" fw={600} ta="center" mt="md">
                    Please fill address to confirm order
                  </Text>
                )}

                <Button 
                  size={isMobileScreen ? 'md' : 'xl'} 
                  radius="md" 
                  fullWidth 
                  mt={!selectedAddress ? 'sm' : 'xl'} 
                  color="green"
                  loading={isLoading}
                  onClick={() => setDeliveryTimeModal(true)}
                  disabled={!selectedAddress}
                  styles={{
                    label: {
                      whiteSpace: 'normal',
                      textAlign: 'center',
                      fontSize: isMobileScreen ? 14 : undefined
                    }
                  }}
                >
                  Confirm & Place Order
                </Button>
                <Text size="xs" color="dimmed" ta="center" mt={10}>
                  By placing the order, you agree to our Terms and Conditions
                </Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>

      <AddAddressModal
        opened={addressModalOpened}
        isEditing={isEditing}
        editData={editData}
        onClose={() => { setAddressModalOpened(false); getDataAddress(); }}
      />
      <DeliveryTimeModal
        opened={deliveryTimeModal}
        onClose={() => setDeliveryTimeModal(false)}
        onSubmit={handleDeliveryTimeSubmit}
      />
    </div>
  );
}

export default CheckoutPage;
